import React, { useEffect, useState } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, Content2Xl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import SiteHeader from "components/headers/SiteHeader.js";
import Footer from "components/footers/MiniCenteredFooter.js";

/* Same background and top spacing as the landing page, carried through the whole page */
const HeaderContainer = tw(Container)`bg-gray-100 -mx-8 -mt-8 pt-8 px-8`;

const Page = tw.div`max-w-screen-md mx-auto py-10 lg:py-12`;

/* Content */
const Article = tw.article``;
const Title = tw.h1`text-2xl font-semibold text-gray-900`;
const Body = styled.div`
  ${tw`mt-4 text-sm text-gray-700`}
  p {
    ${tw`mt-3 leading-relaxed`}
  }
  h2 {
    ${tw`text-base font-semibold text-gray-900 mt-8`}
  }
  ul {
    ${tw`mt-3 list-disc pl-5`}
    li {
      ${tw`mt-2 leading-relaxed`}
      padding-left: 0.25rem;
    }
  }
  strong {
    ${tw`font-medium text-gray-900`}
  }
`;
const Status = tw.p`text-gray-600`;

const slug = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

// Renders **emphasised** segments; everything else is shown exactly as written.
const renderInline = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );

const renderBlock = (block, i) => {
  switch (block.type) {
    case "h":
      return (
        <h2 key={i} id={slug(block.text)}>
          {block.text}
        </h2>
      );
    case "ul":
      return (
        <ul key={i}>
          {block.items.map((item, j) => (
            <li key={j}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    default:
      return <p key={i}>{renderInline(block.text)}</p>;
  }
};

/*
 * Loads the legal text at runtime from /public/legal/<source>, so the approved wording can be
 * replaced without touching any React code or rebuilding the app.
 */
export default ({ source, headingText }) => {
  const [doc, setDoc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setFailed(false);
    fetch(`${process.env.PUBLIC_URL}/legal/${source}`, { cache: "no-cache" })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => !cancelled && setDoc(data))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [source]);

  // Per-page SEO tags, so crawlers see a distinct title, description and canonical URL
  const pageTitle = headingText || (doc && doc.title);
  useEffect(() => {
    if (!pageTitle) return undefined;
    const previousTitle = document.title;
    document.title = `${pageTitle} – Cyber Leeway`;

    const setTag = (selector, create, attr, value) => {
      let el = document.head.querySelector(selector);
      const existed = !!el;
      const previous = existed ? el.getAttribute(attr) : null;
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
      return () => (existed ? el.setAttribute(attr, previous) : el.remove());
    };
    const make = (tag, props) => () => Object.assign(document.createElement(tag), props);

    const restoreDescription = setTag(
      'meta[name="description"]',
      make("meta", { name: "description" }),
      "content",
      `${pageTitle} of Cyber Leeway (PVT) Ltd.`
    );
    const restoreCanonical = setTag(
      'link[rel="canonical"]',
      make("link", { rel: "canonical" }),
      "href",
      `https://cyberleeway.com${window.location.pathname}`
    );
    return () => {
      document.title = previousTitle;
      restoreDescription();
      restoreCanonical();
    };
  }, [pageTitle]);

  return (
    <AnimationRevealPage disabled>
      <HeaderContainer>
        <Content2Xl>
          <SiteHeader />
        </Content2Xl>
        <Page>
          <Article>
            <Title>{headingText || (doc && doc.title)}</Title>
            <Body>
              {doc && doc.blocks.map(renderBlock)}
              {!doc && !failed && <Status>Loading…</Status>}
              {failed && <Status>This page could not be loaded. Please try again later.</Status>}
            </Body>
          </Article>
        </Page>
      </HeaderContainer>
      <Footer />
    </AnimationRevealPage>
  );
};
